// Bring your own key.
//
// The key is typed here, sent to an Edge Function, checked against the provider
// and stored encrypted (ADR-0006). It never reaches PostgREST and it never
// comes back: this screen only ever sees the last four characters, because the
// view it reads has no ciphertext column in it.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { deleteKey, loadKeyStatus, saveKey } from "./api";
import { Loading, Spinner } from "../components/Spinner";
import type { AiKeyStatus } from "../lib/types";

export function ApiKey({
  workspaceId, allowed, canManage,
}: {
  workspaceId: string;
  /** The plan includes BYOK. */
  allowed: boolean;
  /** This person manages billing. */
  canManage: boolean;
}) {
  const [status, setStatus] = useState<AiKeyStatus | null | undefined>(undefined);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadKeyStatus(workspaceId)
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [workspaceId]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await saveKey({ workspaceId, provider: "anthropic", apiKey: key.trim() });
      setKey("");
      setStatus(await loadKeyStatus(workspaceId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setRemoving(true);
    setError(null);
    try {
      await deleteKey(workspaceId);
      setStatus(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="card">
      <h2>Use your own AI key</h2>
      <p className="muted">
        Add your own Anthropic key and drafts stop costing credits — you pay the provider
        directly, at their rates. Everything else stays the same, including which models your
        plan includes.
      </p>

      {!allowed ? (
        <div className="notice upgrade">
          Using your own key is not included in your plan. <Link to="/plans">See plans</Link>
        </div>
      ) : status === undefined ? (
        <Loading label="Checking…" />
      ) : (
        <>
          {status && (
            <div className={`notice${status.status === "failing" ? " upgrade" : ""}`}>
              <strong>
                {status.status === "active" && "Your key is in use."}
                {status.status === "failing" && "Your key stopped working."}
                {status.status === "disabled" && "Your key is switched off."}
                {status.status === "unverified" && "Your key has not been checked yet."}
              </strong>{" "}
              <span className="muted">
                {status.provider} · ending {status.last4}
                {status.verified_at && ` · added ${new Date(status.verified_at).toLocaleDateString()}`}
              </span>
              {/* The one failure that is genuinely worth a paragraph: a key that
                  stops working does not stop drafts, it silently moves them back
                  onto plan credits. Saying so is the difference between a
                  surprise and a decision. */}
              {status.status === "failing" && (
                <p className="muted" style={{ marginTop: ".5rem", marginBottom: 0 }}>
                  {status.last_error ?? "The provider refused it."} Drafts are being written on
                  your plan credits until you add a working key.
                </p>
              )}
            </div>
          )}

          {error && <div className="err">{error}</div>}

          {canManage ? (
            <>
              <label htmlFor="ai-key">
                {status ? "Replace it" : "Anthropic API key"}
              </label>
              <input
                id="ai-key"
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={key}
                placeholder="sk-ant-…"
                onChange={(e) => setKey(e.target.value)}
              />
              <p className="muted" style={{ fontSize: ".82rem", marginTop: ".4rem" }}>
                We check it with the provider before saving, and store it encrypted — the same way
                your connected accounts are stored. It is never shown again.
              </p>
              <div className="row" style={{ marginTop: ".8rem" }}>
                <button className="primary" onClick={submit} disabled={busy || key.trim().length < 20}>
                  {busy && <Spinner />} {busy ? "Checking…" : status ? "Replace key" : "Add key"}
                </button>
                {status && (
                  <button className="danger" onClick={remove} disabled={removing}>
                    {removing && <Spinner />} {removing ? "Removing…" : "Remove key"}
                  </button>
                )}
              </div>
            </>
          ) : (
            <p className="muted">
              Only someone who manages billing for this workspace can add a provider key.
            </p>
          )}
        </>
      )}
    </div>
  );
}
